import { useEffect, useRef } from "react";
import { Grid, Typography, IconButton, Tooltip, useMediaQuery } from "@mui/material";
import AiVedamLogo from "../aivedam.png";
import BridgeupLogo from "../bridgeup.png";
import texhStaLogo from "../staunch.png";
import AloolaLogo from "../aloola.png";
import { ReactComponent as UrlSvg } from "../url.svg";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";

gsap.registerPlugin(ScrollTrigger);

// ── Company data with full details ───────────────────────────────────────────
const companies = [
  {
    num: "01",
    name: "AI Vedam",
    logo: AiVedamLogo,
    role: "Frontend Intern",
    duration: "6 Months",
    year: "2020",
    description:
      "Launched my frontend career building responsive, production-ready React interfaces. Collaborated with senior engineers to deliver UI features, integrated REST APIs for dynamic content, and learned agile workflows in a fast-paced startup environment.",
    tech: ["ReactJS", "HTML & CSS", "JavaScript", "REST APIs", "Git"],
    url: "https://www.linkedin.com/company/aivedam/about/",
    accent: "#00C9A7",
  },
  {
    num: "02",
    name: "Tech Staunch",
    logo: texhStaLogo,
    role: "Frontend Intern",
    duration: "6 Months",
    year: "2021",
    description:
      "Deepened React expertise by architecting complex data-driven components and pixel-perfect responsive layouts. Translated Figma mockups into reusable, accessible UI patterns and improved team component documentation.",
    tech: ["ReactJS", "Redux", "Axios", "SCSS", "Figma"],
    url: "https://techstaunch.com/",
    accent: "#FFB347",
  },
  {
    num: "03",
    name: "Aloola.io",
    logo: AloolaLogo,
    role: "Frontend Lead",
    duration: "10 Months",
    year: "2022",
    description:
      "Led a team of three frontend developers on a B2B SaaS platform serving thousands of users. Architected a scalable component library, integrated real-time GraphQL APIs via Hasura, and drove a 40% improvement in rendering performance through strategic memoisation and code-splitting.",
    tech: ["ReactJS", "GraphQL", "Hasura", "Material-UI", "Redux"],
    url: "https://aloola.io/",
    accent: "#159895",
  },
  {
    num: "04",
    name: "BridgeUp",
    logo: BridgeupLogo,
    role: "Frontend Developer",
    duration: "10 Months",
    year: "2023",
    description:
      "Engineered complex financial dashboard interfaces with real-time data visualisation, interactive Chart.js graphs, and WebSocket-powered live updates. Implemented lazy-loading and bundle splitting strategies that cut initial load time by 35% on a high-traffic B2B product.",
    tech: ["ReactJS", "Chart.js", "Material-UI", "WebSockets", "TypeScript"],
    url: "https://bridgeup.com/",
    accent: "#7C3AED",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// DESKTOP — horizontal pinned scroll with per-card GSAP animations
// ─────────────────────────────────────────────────────────────────────────────
const DesktopExperience = () => {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const progressBarRef = useRef(null);
  const dotsRef = useRef([]);
  const counterRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;

    const ctx = gsap.context(() => {
      // ── 1. Horizontal scroll — section stays pinned, track slides left ──────
      const mainTween = gsap.to(track, {
        x: () => -(track.scrollWidth - window.innerWidth),
        ease: "none",
        scrollTrigger: {
          id: "h-exp",
          trigger: section,
          pin: true,
          anticipatePin: 1,
          start: "top top",
          end: () => `+=${track.scrollWidth - window.innerWidth}`,
          scrub: 1.2,
          onUpdate: (self) => {
            // Progress bar
            if (progressBarRef.current) {
              progressBarRef.current.style.width = `${self.progress * 100}%`;
            }
            // Counter + dots
            const idx = Math.min(
              Math.floor(self.progress * companies.length + 0.05),
              companies.length - 1
            );
            if (counterRef.current) {
              counterRef.current.textContent = `0${idx + 1} / 0${companies.length}`;
            }
            dotsRef.current.forEach((dot, i) => {
              if (!dot) return;
              const active = i === idx;
              dot.style.opacity = active ? "1" : "0.25";
              dot.style.transform = active ? "scale(1.6)" : "scale(1)";
              dot.style.background = active ? companies[i].accent : "currentColor";
            });
          },
        },
      });

      // ── 2. Per-card element animations (triggered by horizontal position) ───
      // containerAnimation must be the Tween (mainTween), not mainTween.scrollTrigger.
      // ScrollTrigger.init calls containerAnimation.time.bind() — only Tweens have .time().
      gsap.utils.toArray(".exp-card").forEach((card) => {
        const base = {
          trigger: card,
          containerAnimation: mainTween,
          start: "left 88%",
          toggleActions: "play none none reverse",
        };

        // Card number — animate to 0.4 to match intended opacity (not 1, which
        // overrides the sx CSS class and makes the label too prominent)
        gsap.fromTo(
          card.querySelector(".ec-num"),
          { x: -70, opacity: 0 },
          { x: 0, opacity: 0.4, duration: 0.75, ease: "power3.out", scrollTrigger: base }
        );

        // Separator line draws in
        gsap.fromTo(
          card.querySelector(".ec-line"),
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 0.85,
            ease: "power3.out",
            transformOrigin: "left center",
            scrollTrigger: { ...base, start: "left 86%" },
          }
        );

        // Logo box scales in
        gsap.fromTo(
          card.querySelector(".ec-logo-box"),
          { scale: 0.65, opacity: 0 },
          {
            scale: 1,
            opacity: 1,
            duration: 0.9,
            ease: "back.out(1.7)",
            scrollTrigger: { ...base, start: "left 82%" },
          }
        );

        // Company name + role
        gsap.fromTo(
          card.querySelector(".ec-role"),
          { y: 45, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.7,
            ease: "power3.out",
            scrollTrigger: { ...base, start: "left 78%" },
          }
        );

        // Description — animate to 0.68 to match sx opacity: 0.68
        gsap.fromTo(
          card.querySelector(".ec-desc"),
          { y: 30, opacity: 0 },
          {
            y: 0,
            opacity: 0.68,
            duration: 0.65,
            ease: "power3.out",
            scrollTrigger: { ...base, start: "left 72%" },
          }
        );

        // Tech tags stagger
        gsap.fromTo(
          card.querySelectorAll(".ec-tag"),
          { y: 18, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.07,
            duration: 0.5,
            ease: "power2.out",
            scrollTrigger: { ...base, start: "left 65%" },
          }
        );

        // Link button
        gsap.fromTo(
          card.querySelector(".ec-link"),
          { scale: 0.7, opacity: 0 },
          {
            scale: 1,
            opacity: 1,
            duration: 0.55,
            ease: "back.out(2)",
            scrollTrigger: { ...base, start: "left 60%" },
          }
        );

        // Watermark number — fades in slower
        gsap.fromTo(
          card.querySelector(".ec-watermark"),
          { opacity: 0 },
          {
            opacity: 0.05,
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: { ...base, start: "left 85%" },
          }
        );
      });
    }, section);

    // Recalculate all trigger positions after layout is complete
    ScrollTrigger.refresh();

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="experience"
      style={{ height: "100vh", overflow: "hidden", position: "relative" }}
    >
      {/* ── Fixed HUD: section label + card counter ── */}
      <div
        style={{
          position: "absolute",
          top: 24,
          left: 0,
          right: 0,
          zIndex: 10,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 80px",
          pointerEvents: "none",
        }}
      >
        <Typography
          fontFamily="Alkatra"
          fontSize={13}
          fontWeight={700}
          sx={{ letterSpacing: "0.28em", textTransform: "uppercase", opacity: 0.45 }}
        >
          Experience
        </Typography>
        <Typography
          ref={counterRef}
          fontFamily="Poppins"
          fontSize={13}
          fontWeight={500}
          sx={{ opacity: 0.45 }}
        >
          01 / 04
        </Typography>
      </div>

      {/* ── Scrolling track (400 vw) ── */}
      <div
        ref={trackRef}
        style={{ display: "flex", height: "100%", willChange: "transform" }}
      >
        {companies.map((c, i) => (
          <div
            key={i}
            className="exp-card"
            style={{
              width: "100vw",
              height: "100%",
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              padding: "100px 9% 90px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Ghost watermark number */}
            <div
              className="ec-watermark"
              style={{
                position: "absolute",
                bottom: -20,
                right: 40,
                fontSize: "30vw",
                fontWeight: 900,
                opacity: 0,
                fontFamily: "Poppins",
                lineHeight: 1,
                userSelect: "none",
                pointerEvents: "none",
                letterSpacing: "-0.06em",
              }}
            >
              {c.num}
            </div>

            {/* Card index label */}
            <Typography
              className="ec-num"
              fontFamily="Poppins"
              fontSize={12}
              fontWeight={600}
              sx={{ letterSpacing: "0.3em", textTransform: "uppercase", mb: 1 }}
            >
              {c.num}
            </Typography>

            {/* Accent separator line */}
            <div
              className="ec-line"
              style={{
                height: 1.5,
                width: "38%",
                background: `linear-gradient(90deg, ${c.accent}, transparent)`,
                marginBottom: 48,
              }}
            />

            {/* Two-column content */}
            <Grid container spacing={6} alignItems="center">
              {/* Left: logo */}
              <Grid item xs={12} md={4} lg={3}>
                <div
                  className="ec-logo-box"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "44px 36px",
                    borderRadius: 20,
                    border: `1px solid ${c.accent}33`,
                    background: `${c.accent}0a`,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Subtle glow behind logo */}
                  <div
                    style={{
                      position: "absolute",
                      inset: "30%",
                      background: c.accent,
                      opacity: 0.12,
                      filter: "blur(28px)",
                      borderRadius: "50%",
                      pointerEvents: "none",
                    }}
                  />
                  <img
                    src={c.logo}
                    alt={c.name}
                    style={{ maxHeight: 52, maxWidth: "100%", objectFit: "contain", position: "relative", zIndex: 1 }}
                  />
                </div>
              </Grid>

              {/* Right: details */}
              <Grid item xs={12} md={8} lg={9}>
                {/* Company name + role + meta */}
                <div className="ec-role">
                  <Typography
                    fontFamily="Alkatra"
                    fontWeight={700}
                    sx={{ fontSize: { md: "38px", lg: "50px" }, lineHeight: 1.05, mb: 1 }}
                  >
                    {c.name}
                  </Typography>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 22 }}>
                    <Typography
                      fontFamily="Poppins"
                      fontWeight={600}
                      fontSize={17}
                      sx={{ color: c.accent }}
                    >
                      {c.role}
                    </Typography>
                    <span style={{ opacity: 0.25, fontSize: 18 }}>·</span>
                    <Typography fontFamily="Poppins" fontSize={14} sx={{ opacity: 0.5 }}>
                      {c.duration}
                    </Typography>
                    <span style={{ opacity: 0.25, fontSize: 18 }}>·</span>
                    <Typography fontFamily="Poppins" fontSize={14} sx={{ opacity: 0.5 }}>
                      {c.year}
                    </Typography>
                  </div>
                </div>

                {/* Description */}
                <Typography
                  className="ec-desc"
                  fontFamily="Poppins"
                  fontSize={15}
                  sx={{ lineHeight: 1.8, mb: 3, maxWidth: 580 }}
                >
                  {c.description}
                </Typography>

                {/* Tech stack */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 28 }}>
                  {c.tech.map((t) => (
                    <span
                      key={t}
                      className="ec-tag"
                      style={{
                        padding: "5px 15px",
                        borderRadius: 100,
                        fontSize: 12,
                        fontFamily: "Poppins",
                        fontWeight: 500,
                        border: `1px solid ${c.accent}`,
                        color: c.accent,
                        letterSpacing: "0.02em",
                      }}
                    >
                      {t}
                    </span>
                  ))}
                </div>

                {/* Visit link */}
                <Tooltip title="Visit Company" arrow>
                  <IconButton
                    className="ec-link"
                    href={c.url}
                    target="_blank"
                    sx={{
                      border: `1px solid ${c.accent}44`,
                      padding: "10px",
                      transition: "box-shadow 0.35s, border-color 0.35s",
                      "&:hover": {
                        borderColor: c.accent,
                        boxShadow: `0 0 22px 3px ${c.accent}55`,
                      },
                    }}
                  >
                    <UrlSvg />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>

            {/* Bottom accent gradient bar */}
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: 3,
                background: `linear-gradient(90deg, ${c.accent} 0%, transparent 100%)`,
              }}
            />
          </div>
        ))}
      </div>

      {/* ── Progress dots ── */}
      <div
        style={{
          position: "absolute",
          bottom: 30,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 10,
          alignItems: "center",
          zIndex: 10,
          pointerEvents: "none",
        }}
      >
        {companies.map((_, i) => (
          <div
            key={i}
            ref={(el) => (dotsRef.current[i] = el)}
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: i === 0 ? companies[0].accent : "currentColor",
              opacity: i === 0 ? 1 : 0.25,
              transition: "all 0.3s ease",
            }}
          />
        ))}
      </div>

      {/* ── Scroll progress bar ── */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 2,
          background: "rgba(128,128,128,0.15)",
          zIndex: 5,
        }}
      >
        <div
          ref={progressBarRef}
          style={{ height: "100%", background: "#159895", width: "0%", transition: "width 0.05s linear" }}
        />
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MOBILE — vertical stacking cards with Framer Motion
// ─────────────────────────────────────────────────────────────────────────────
const MobileExperience = () => (
  <div id="experience" style={{ padding: "0 20px 60px" }}>
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
    >
      <Grid
        item
        style={{ borderRadius: "0px 40px 0px 40px" }}
        mt={10}
        mb={5}
        alignItems="center"
        justifyContent="center"
        display="flex"
        xs={12}
        border="solid 1px"
      >
        <Typography fontFamily="Alkatra" fontSize={30} fontWeight={700}>
          Experience
        </Typography>
      </Grid>
    </motion.div>

    <div style={{ display: "flex", flexDirection: "column", gap: 20, alignItems: "center" }}>
      {companies.map((c, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.65, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
          style={{
            width: "100%",
            maxWidth: 540,
            borderRadius: 16,
            padding: "28px 24px",
            position: "relative",
            overflow: "hidden",
            border: `1px solid ${c.accent}33`,
            borderBottom: `3px solid ${c.accent}`,
          }}
        >
          {/* Logo + header */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                background: `${c.accent}10`,
                border: `1px solid ${c.accent}33`,
                flexShrink: 0,
              }}
            >
              <img src={c.logo} alt={c.name} height={24} style={{ objectFit: "contain", display: "block" }} />
            </div>
            <div>
              <Typography fontFamily="Alkatra" fontWeight={700} fontSize={20}>{c.name}</Typography>
              <Typography fontFamily="Poppins" fontSize={12} sx={{ color: c.accent, fontWeight: 600 }}>
                {c.role}
              </Typography>
              <Typography fontFamily="Poppins" fontSize={11} sx={{ opacity: 0.5 }}>
                {c.duration} · {c.year}
              </Typography>
            </div>
          </div>

          <Typography fontFamily="Poppins" fontSize={13} sx={{ opacity: 0.7, lineHeight: 1.75, mb: 2 }}>
            {c.description}
          </Typography>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
            {c.tech.map((t) => (
              <span
                key={t}
                style={{
                  padding: "3px 10px",
                  borderRadius: 100,
                  fontSize: 11,
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  border: `1px solid ${c.accent}`,
                  color: c.accent,
                }}
              >
                {t}
              </span>
            ))}
          </div>

          <Tooltip title="Visit Company" arrow>
            <IconButton
              href={c.url}
              target="_blank"
              sx={{
                padding: "8px",
                border: `1px solid ${c.accent}44`,
                "&:hover": { boxShadow: `0 0 18px 2px ${c.accent}55` },
              }}
            >
              <UrlSvg />
            </IconButton>
          </Tooltip>
        </motion.div>
      ))}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
const Experience = () => {
  const isMobile = useMediaQuery("(max-width:899px)");
  return isMobile ? <MobileExperience /> : <DesktopExperience />;
};

export default Experience;
